import { FC, useEffect, useRef } from 'react';
import { useClassName } from '../../hooks/use-class-name';
import SpriteIcon from '../shared/sprite-icon';
import { useTranslation } from '../../hooks/use-translation';
import { useBuildDownloadUrl } from '../../hooks/use-download-url-builder';
import downLoadAnimation from './animations/download-icon.json';
import { OwApp } from '../../interfaces/domain/ow-app.modal';
import OWLink from '../shared/ow-link';
import {
  useDownloadClickHandler,
  DownloadClickComponent,
} from '../../hooks/use-download-click-handler';
import { useInstalledExtensions } from '../../hooks/use-installed-extensions';

// -----------------------------------------------------------------------------
interface Props {
  isOpen: boolean;
  onClickToggleOpen: () => void;
  onClickClose: () => void;
  currentApp: OwApp;
  url?: string;
  isDeepLink?: boolean;
}

// -----------------------------------------------------------------------------
export const DownloadModal: FC<Props> = ({
  isOpen,
  onClickToggleOpen,
  onClickClose,
  currentApp,
  url = null,
  isDeepLink = false,
}): JSX.Element => {
  const animIconContainer = useRef<HTMLDivElement>(null);
  const currentAppId = currentApp?.id ?? null;
  const currentAppTitle = currentApp?.title ?? null;
  const constructedUrl = useBuildDownloadUrl({
    name: currentAppTitle,
    extensionId: currentAppId,
    utmContent: 'new-light',
    utmSource: 'web_app_store',
  });

  const downloadUrl = url ? url : constructedUrl;

  const { handleClickDownload } = useDownloadClickHandler({
    item: currentApp,
    component: DownloadClickComponent.DownloadModal,
    enableDeepLink: false,
  });

  const { t } = useTranslation();
  const { installedAppIds } = useInstalledExtensions();

  const clickedAlt = currentApp?.alternativeDownloads?.find((d) => {
    if (d.appId && url?.includes(d.appId)) {
      return true;
    }

    if (d.url && url?.includes(d.url)) {
      return true;
    }

    return false;
  });

  const effectiveAppId = clickedAlt?.appId ?? currentAppId;
  const isClickedElectron =
    clickedAlt?.isElectron ?? currentApp?.isElectron ?? false;

  const isClickedWindows = clickedAlt
    ? clickedAlt.platform === 'Windows'
    : true;

  const isInstalled = !!effectiveAppId && installedAppIds.has(effectiveAppId);

  const className = useClassName(
    {
      'modal-is-open': isOpen,
    },
    'download-modal',
  );

  useEffect(() => {
    if (!isOpen) return;
    let animation: any;
    void import('lottie-web').then((module) => {
      const lottie = module.default;
      if (animIconContainer.current) {
        animIconContainer.current.innerHTML = '';
      }
      animation = lottie.loadAnimation({
        container: animIconContainer.current,
        animationData: downLoadAnimation,
        renderer: 'svg',
        loop: true,
        autoplay: true,
        name: 'download-icon',
      });
    });

    return () => {
      if (animation) {
        animation.destroy();
      }
      if (animIconContainer.current) {
        animIconContainer.current.innerHTML = '';
      }
    };
  }, [isOpen]);

  const getModalText = (): string => {
    if (!isDeepLink && isInstalled && !isClickedElectron && isClickedWindows) {
      return t('owClientNotInstalled');
    }

    if (!isDeepLink) {
      return t('downloadStart');
    }

    if (isInstalled) {
      return t('deepLinkLaunch');
    }

    return t('deepLinkStart');
  };

  const modalText = getModalText();

  return (
    <div className={className}>
      <div className="download-modal-main">
        <button
          className="clean-icon-btn close-modal"
          onClick={onClickToggleOpen}
        >
          <SpriteIcon icon="closeSmall" />
        </button>

        <div className="download-modal-content">
          <div>
            <div
              id="download-icon"
              className="anim-icon"
              ref={animIconContainer}
            />
            <h2>{modalText}</h2>
            <p>
              {t('ifItDoesnt')}
              <OWLink
                onClick={(e) =>
                  handleClickDownload(e as any, downloadUrl, true)
                }
                href={downloadUrl}
                download={t('appInstaller')}
              >
                {t('dlHere')}
              </OWLink>
            </p>
          </div>

          <div>
            <p
              className="small"
              dangerouslySetInnerHTML={{
                __html: t('dlSupport'),
              }}
            />
          </div>
        </div>
      </div>
      <div className="bg" onClick={onClickClose} />
    </div>
  );
};
